import React from 'react';
import I18n from 'i18n-js';
import { gql } from '@apollo/client';
import { isEmpty } from 'lodash';

import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { RCButton, RCTextWithIcon } from '@reverbdotcom/cadence/components';
import { Listing } from '@reverbdotcom/commons/src/gql/graphql';
import { ModalDialog } from '@reverbdotcom/commons';
import { CheckCircleIcon } from '@reverbdotcom/cadence/icons/react';
import { useIsMobileWindowWidth } from '@reverbdotcom/cadence/components/helpers/is-mobile';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';

export const COMPONENT_NAME = 'BulkPublishButton';
interface IExternalProps {
  selectedListings: Listing[];
  onSuccessCallback: (listingResponses) => void;
  onErrorCallback: (errorMessage: string) => void;
}

const BULK_PUBLISH_MUTATION = gql`
  mutation Core_MyListings_BulkPublish(
    $input: Input_core_apimessages_BulkPublishListingsRequest
  ) {
    bulkPublishListings(input: $input) {
      listings {
        listingId
        success
        publishErrors {
          message
        }
      }
      prepublishStepRequired {
        step
      }
    }
  }
`;

export default function BulkPublishButton({
  selectedListings,
  onSuccessCallback,
  onErrorCallback,
}: IExternalProps) {
  const isMobile = useIsMobileWindowWidth();

  const listingIds = selectedListings.map(listing => listing.id);

  const publishableListings = selectedListings.filter(listing => listing.publishValidation.isValid);
  const unpublishableListings = selectedListings.filter(listing => !listing.publishValidation.isValid);

  const hasPublishableListings = !!publishableListings.length;
  const hasNoPublishableListings = unpublishableListings.length === selectedListings.length;
  const hasUnpublishableListings = !!unpublishableListings.length;

  const modalSubmitI18nKey = hasNoPublishableListings ? 'close' : 'publish';

  const [isErrorModalOpen, showErrorModal] = React.useState(false);

  const [bulkPublishListings, {
    data = {},
    loading,
    errors,
  }] = useMutation(BULK_PUBLISH_MUTATION);

  const { listings: listingsResponse, prepublishStepRequired } = data.bulkPublishListings ?? {};
  const hasErrors = !!errors.length;

  React.useEffect(() => {
    if (loading) return;
    if (hasErrors) return handleUnexpectedError();

    handleResponse();
  }, [loading]);

  function handleResponse() {
    if (isEmpty(data)) return;

    if (prepublishStepRequired?.step) {
      onErrorCallback(
        I18n.t('discovery.listingsManagement.toolbar.bulkActions.bulkPublish.prepublishStepRequired'),
      );
    } else {
      onSuccessCallback(listingsResponse);
    }
  }

  function handleUnexpectedError() {
    onErrorCallback(
      I18n.t('discovery.listingsManagement.genericError'),
    );
  }

  function publish() {
    const publishableListingIds = publishableListings.map(listing => listing.id);

    trackEvent({
      componentName: COMPONENT_NAME,
      eventName: MParticleEventName.PublishingListings,
      listingIds: publishableListingIds,
    });

    bulkPublishListings({
      variables: {
        input: {
          listingIds: publishableListingIds,
        },
      },
    });
  }

  function handleDefaultSubmit() {
    trackEvent({
      eventName: MParticleEventName.ClickedComponent,
      componentName: COMPONENT_NAME,
    });

    if (hasUnpublishableListings) {
      showErrorModal(true);
    } else {
      publish();
    }
  }

  function handleModalSubmit() {
    showErrorModal(false);

    if (!hasNoPublishableListings) {
      publish();
    }
  }

  useViewTracking({
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ComponentView,
  }, true);

  return (
    <>
      <RCButton
        fullWidth
        variant="outlined"
        size={isMobile ? 'medium' : 'mini' }
        onClick={handleDefaultSubmit}
        disabled={!listingIds.length || loading}
      >
        <RCTextWithIcon svgComponent={CheckCircleIcon} placement="right">
          {I18n.t('discovery.listingsManagement.toolbar.bulkActions.bulkPublish.publishSelected')}
        </RCTextWithIcon>
      </RCButton>
      <ModalDialog
        isOpen={isErrorModalOpen}
        headerTitle={I18n.t('discovery.listingsManagement.toolbar.bulkActions.bulkPublish.publishErrorModal.headerTitle', {
          count: publishableListings.length,
        })}
        saveButtonText={I18n.t(`discovery.listingsManagement.toolbar.bulkActions.bulkPublish.publishErrorModal.${modalSubmitI18nKey}`)}
        hideFooterDismissButton={hasNoPublishableListings}
        onSubmit={handleModalSubmit}
        onRequestClose={() => showErrorModal(false)}
      >
        {hasPublishableListings && (
          <div className="weight-bold mb-4">
            {I18n.t('discovery.listingsManagement.toolbar.bulkActions.bulkPublish.publishErrorModal.publishMessage', {
              count: publishableListings.length,
            })}
          </div>
        )}

        <div className="weight-bold">
          {I18n.t('discovery.listingsManagement.toolbar.bulkActions.bulkPublish.publishErrorModal.errorMessage', {
            count: unpublishableListings.length,
          })}
        </div>
      </ModalDialog>
    </>
  );
}
